import React, { useEffect, useState } from 'react';
import { Icon } from './inboxIcons';

const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// One attachment, read through our backend: email files from Gmail, or WhatsApp media
// (pass `path`). Images show as a preview; voice notes and videos play inline.
function InboxAttachment({ att, ticketId, fetchBlob, path: given }) {
  const mime = att.mimeType || '';
  const isImage = mime.startsWith('image/');
  const playable = /^(audio|video)\//.test(mime) ? mime.split('/')[0] : null;
  const name = att.filename || (att.voice ? 'Voice note' : att.type || 'file');
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const path = given || `/api/v1/support/tickets/${ticketId}/email-attachments/${att.gmailMsgId}/${encodeURIComponent(att.partId)}`;

  useEffect(() => {
    if (!isImage) return undefined;
    let alive = true;
    let made = null;
    fetchBlob(path).then((b) => { made = URL.createObjectURL(b); if (alive) setUrl(made); }).catch(() => alive && setError(true));
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [isImage, path, fetchBlob]);

  // Voice notes / videos load on first play, then stay in place
  useEffect(() => () => { if (playable && url) URL.revokeObjectURL(url); }, [playable, url]);
  const play = async () => {
    setBusy(true);
    try {
      setUrl(URL.createObjectURL(await fetchBlob(path)));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  const open = async () => {
    if (url) { window.open(url, '_blank', 'noopener'); return; }
    setBusy(true);
    try {
      const b = await fetchBlob(path);
      const u = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = u;
      a.target = '_blank';
      a.rel = 'noopener';
      if (!/pdf|text|image/.test(mime)) a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  if (isImage && url && !error) {
    return (
      <button type="button" className="cx-att-img" onClick={open} title={`${name}${att.size ? ` · ${kb(att.size)}` : ''}`}>
        <img src={url} alt={name} />
      </button>
    );
  }
  if (playable && url && !error) {
    return playable === 'audio'
      ? <audio className="cx-att-audio" src={url} controls autoPlay />
      : <video className="cx-att-video" src={url} controls autoPlay />;
  }
  return (
    <button type="button" className="cx-att" onClick={playable ? play : open} disabled={busy} title={playable ? `Play ${name}` : 'Open attachment'}>
      <Icon name="attach" size={15} />
      <span>{playable ? `Play ${att.voice ? 'voice note' : playable}` : name}</span>
      <small>{error ? 'could not load' : busy ? 'loading…' : att.size ? kb(att.size) : ''}</small>
    </button>
  );
}

export default InboxAttachment;
